<template>
	<img :src="src" alt="Mutual of Enumclaw" class="logo" />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useTheme } from 'vuetify'

/**
 * The Mutual of Enumclaw mark, from the official logo suite in
 * `@moe-tech/design`.
 *
 * Uses the **Badge** lockup deliberately: it is the mark on its own, with no
 * "Mutual of Enumclaw" wordmark and no "MOE" lettering. (The suite's `Badge MOE`
 * adds the letters, and `Horizontal`/`Stacked` add the full name — none of those
 * belong here, where the mark sits beside a page title or inside a spinner.)
 *
 * SVG only. The suite also ships PNGs; they are not used anywhere in this app.
 *
 * Light/dark swaps the whole file rather than recoloring one. The mark is
 * multi-color — Sky Blue, Green Apple and three Alpine Green bands — and those
 * accents must NOT be re-tinted. The brand's own dark treatment is the `Reverse`
 * variant, which turns only the deep-green outline white and leaves the accents
 * untouched. That behaviour is also the evidence the app's dark theme was
 * derived from; see docs/color-selection.md §4.1.
 */
import badgeColor from '@moe-tech/design/logos/svg/MOE Logo-RGB_Badge-Color.svg'
import badgeReverse from '@moe-tech/design/logos/svg/MOE Logo-RGB_Badge-Reverse.svg'

const theme = useTheme()

const src = computed(() => (theme.global.current.value.dark ? badgeReverse : badgeColor))
</script>

<style scoped>
.logo {
	/* A square box, matching the footprint of the SVG this replaced — that one */
	/* had a near-square viewBox, so 30x30 filled it. The badge is portrait */
	/* (209.822 x 278.129), so `object-fit: contain` letterboxes it inside the */
	/* square and centres it, instead of `width: auto` shrinking the box to */
	/* 22.6px and pulling the mark off-centre in the app bar. */
	height: var(--app-size-logo-h);
	width: var(--app-size-logo-w);
	object-fit: contain;
	/* Keeps it centred in an inline context too, not just a flex one. */
	vertical-align: middle;
}
</style>
